import { useState, useCallback } from 'react';
import { RealtimeParkingViewModel } from '../../../viewmodels/RealtimeParkingViewModel';
import { CctvTemplate } from '../../../models/CctvTemplate';

/** 맵에서 뽑은 CCTV 한 건 — MapCanvasPanel 이 보고하는 모양. */
export interface MapCctvInput {
  name: string;
  ip: string;
}

export function useRealtimeCctvTemplate(projectId: string) {
  const [cctvTemplate, setCctvTemplate] = useState<CctvTemplate | null>(null);
  const [templateBasedMode, setTemplateBasedMode] = useState(false);
  const [cctvList, setCctvList] = useState<string[]>([]);
  const [selectedCctv, setSelectedCctv] = useState<string>('');
  const [selectedCctvImages, setSelectedCctvImages] = useState<any>(null);
  const [cctvImageError, setCctvImageError] = useState<string | null>(null);
  const [imageUpdateKey, setImageUpdateKey] = useState(0);

  const applyTemplate = useCallback((template: CctvTemplate) => {
    setCctvTemplate(template);
    setTemplateBasedMode(true);
    const templateCctvs = template.cctvList.map(cctv => cctv.cctvId);
    setCctvList(templateCctvs);
    if (template.cctvList.length > 0) {
      setSelectedCctv(template.cctvList[0].cctvId);
    }
  }, []);

  const loadCctvTemplate = useCallback(async () => {
    try {
      console.log('Loading CCTV template for projectId:', projectId);

      const template = RealtimeParkingViewModel.getCctvTemplate(projectId);
      if (template) {
        setCctvTemplate(template);
        setTemplateBasedMode(true);
        console.log('CCTV \uD15C\uD50C\uB9BF \uB85C\uB4DC \uC644\uB8CC:', template);

        const templateCctvs = template.cctvList.map(cctv => cctv.cctvId);
        setCctvList(templateCctvs);
        console.log('CCTV \uB9AC\uC2A4\uD2B8 \uC124\uC815:', templateCctvs);

        if (template.cctvList.length > 0) {
          const firstCctv = template.cctvList[0].cctvId;
          setSelectedCctv(firstCctv);

          try {
            const images = RealtimeParkingViewModel.getTemplateBasedCctvImages(projectId, firstCctv, template);
            setSelectedCctvImages(images);
            setImageUpdateKey(prev => prev + 1);
            setCctvImageError(null);
          } catch (error: any) {
            console.error('\uCCAB \uBC88\uC9F8 CCTV \uC774\uBBF8\uC9C0 \uB85C\uB4DC \uC2E4\uD328:', error);
            setCctvImageError(error.message || 'CCTV \uC774\uBBF8\uC9C0 \uB85C\uB4DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
          }
        }
      } else {
        console.warn(`\uD504\uB85C\uC81D\uD2B8 "${projectId}"\uC758 CCTV \uD15C\uD50C\uB9BF\uC774 \uC5C6\uC2B5\uB2C8\uB2E4. banpo \uD15C\uD50C\uB9BF \uC0AC\uC6A9`);
        const fallback = RealtimeParkingViewModel.getCctvTemplate('banpo');
        if (fallback) {
          applyTemplate(fallback);
        }
      }
    } catch (error) {
      console.error('CCTV \uD15C\uD50C\uB9BF \uB85C\uB4DC \uC2E4\uD328:', error);
      const fallback = RealtimeParkingViewModel.getCctvTemplate('banpo');
      if (fallback) {
        applyTemplate(fallback);
      }
    }
  }, [projectId, applyTemplate]);

  /**
   * 맵에 배치된 CCTV 로 목록을 갈아끼운다 — 현장별 하드코딩 JSON 대신 맵을 출처로 쓴다.
   */
  const applyMapCctvs = useCallback((cctvs: MapCctvInput[], mapName: string) => {
    if (cctvs.length === 0) return;
    applyTemplate(RealtimeParkingViewModel.buildMapCctvTemplate(projectId, mapName, cctvs));
  }, [projectId, applyTemplate]);

  /** 수동 새로고침 — 폴링을 기다리지 않고 지금 다시 받는다. */
  const refreshImages = useCallback(() => {
    if (!selectedCctv || !cctvTemplate) return;
    try {
      const images = RealtimeParkingViewModel.getTemplateBasedCctvImages(
        projectId,
        selectedCctv,
        cctvTemplate,
      );
      setSelectedCctvImages(images);
      setImageUpdateKey(prev => prev + 1);
      setCctvImageError(null);
    } catch (error: any) {
      setCctvImageError(error.message || 'CCTV 이미지 로드 중 오류가 발생했습니다.');
    }
  }, [projectId, selectedCctv, cctvTemplate]);

  const handleCctvSelect = useCallback((cctvId: string) => {
    setSelectedCctv(cctvId);

    if (templateBasedMode && cctvTemplate) {
      try {
        const images = RealtimeParkingViewModel.getTemplateBasedCctvImages(projectId, cctvId, cctvTemplate);
        setSelectedCctvImages(images);
        setImageUpdateKey(prev => prev + 1);
        setCctvImageError(null);
      } catch (error: any) {
        console.error('\uD15C\uD50C\uB9BF \uAE30\uBC18 CCTV \uC774\uBBF8\uC9C0 \uB85C\uB4DC \uC2E4\uD328:', error);
        setCctvImageError(error.message || 'CCTV \uC774\uBBF8\uC9C0 \uB85C\uB4DC \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
      }
    }
  }, [templateBasedMode, cctvTemplate, projectId]);

  return {
    cctvTemplate,
    templateBasedMode,
    cctvList,
    selectedCctv,
    selectedCctvImages,
    setSelectedCctvImages,
    cctvImageError,
    setCctvImageError,
    imageUpdateKey,
    setImageUpdateKey,
    loadCctvTemplate,
    handleCctvSelect,
    setCctvList,
    applyMapCctvs,
    refreshImages,
    setSelectedCctv,
  };
}
